/* ENSSA Reporting — Class page (Level 3). */

import React from "react";
import {
  Card, CSVExportButton, Section,
  RiskBadge, StatusBadge, TermToggle,
} from "@/components/branded/Components";
import { RiskPyramid } from "@/components/charts/Charts";
import { PercentileRankMiniBar } from "@/components/charts/PercentileRankMiniBar";
import { RiskTransitionSankey } from "@/components/charts/sankey";
import { ClassTaskFamilyTable } from "@/components/branded/ClassTaskFamilyTable";
import { StudentSubtestCards } from "@/components/branded/StudentSubtestCards";
import { useMediaQuery } from "@/lib/useMediaQuery";
import { useTracker } from "@/hooks/useTracker";
import {
  TERM_DEFAULT, getBattery,
} from "@/data/tokens";
import {
  availableTermIds, defaultAvailableTermId, riskCountsOf, screenerStatusText,
  getMultiStageTransitions, getStudentAssessment,
} from "@/lib/report-data";
import { taskFamilyDistributionsForBattery } from "@/lib/task-family-bands";
import { overallScorePointsForTerm, type OverallScorePoint } from "@/lib/overall-score-points";
import { riskLabel, PORTAL_TERMS } from "@/lib/copy";
import { classStudentCsvRows, downloadCsv } from "@/lib/class-csv";
import type {
  AggregateNode, AssessmentForTerm, ClassRoom, RiskCounts, School, StatusCounts, Student, TermId,
} from "@/types";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { DemographicFilterBar } from "@/components/branded/DemographicFilterBar";
import { NO_DEMOGRAPHIC_FILTERS, makeDemographicPredicate } from "@/lib/demographic-filters";
import type { DemographicFilters } from "@/lib/demographic-filters";

const { useState: useCS, useMemo } = React;

function percentileRankLabel(percentile: number): string {
  return String(Math.round(percentile));
}

function percentileLabel(percentile: number): string {
  return `PR${Math.round(percentile)}`;
}

const OVERALL_PERCENTILE_BAND_CUTOFFS = { veryLowMax: 15, lowMax: 35 } as const;
const OVERALL_PERCENTILE_SCALE_NOTE = "Percentile Rank shows how a student's overall ENSSA performance compares with other students of the same year level. They do not represent the percentage of questions answered correctly. Lower percentile ranks may indicate an increased risk of difficulty in number sense development and may warrant further monitoring or support.";
const RECOMMENDATION_BASE_URL = "https://enssa.app/recommendations/";
const RECOMMENDATION_CAMPAIGN = "2026_t2";

function classRecommendationRid(klass: ClassRoom): string | null {
  if (klass.classRecommendationRid) return klass.classRecommendationRid;
  if (!klass.classRecommendationsUrl) return null;
  try {
    return new URL(klass.classRecommendationsUrl).searchParams.get("rid");
  } catch {
    return null;
  }
}

function classRecommendationHref(klass: ClassRoom): string {
  if (klass.classRecommendationsUrl) return klass.classRecommendationsUrl;
  const focusTaskIds = klass.classRecommendationFocusTaskIds ?? [];
  const params = [
    `year=${encodeURIComponent(klass.year)}`,
    ...(focusTaskIds.length ? [`focus=${focusTaskIds.map(encodeURIComponent).join(",")}`] : []),
    ...(klass.classRecommendationRid ? [`rid=${encodeURIComponent(klass.classRecommendationRid)}`] : []),
    "utm_source=janison_portal",
    "utm_medium=class_report",
    `utm_campaign=${RECOMMENDATION_CAMPAIGN}`,
  ];
  return `${RECOMMENDATION_BASE_URL}?${params.join("&")}`;
}

function classRecommendationTrackingProps(
  klass: ClassRoom,
  source: "recommendation_card" | "subtest_focus_chip",
  taskId: string | null = null,
): Record<string, string | null> {
  return {
    source,
    class_id: klass.id,
    rid: classRecommendationRid(klass),
    focus_task_ids: (klass.classRecommendationFocusTaskIds ?? []).join(","),
    task_id: taskId,
  };
}

function PercentileCutoffFootnote() {
  return (
    <>
      PR means Percentile Rank. PR15 and below is{' '}
      <strong style={{ color: 'var(--risk-concern-fg)', whiteSpace: 'nowrap' }}>Very Low</strong>, PR16 to PR35 is{' '}
      <strong style={{ color: 'var(--risk-emerging-fg)', whiteSpace: 'nowrap' }}>Low</strong>, and PR36 and above is{' '}
      <strong style={{ color: 'var(--risk-on-track-fg)', whiteSpace: 'nowrap' }}>On Track</strong>.
    </>
  );
}

interface OverallScoreDotPlotProps {
  points: OverallScorePoint[];
  onOpenStudent: (id: string, term?: TermId | string) => void;
  bandCutoffs: { veryLowMax: number; lowMax: number };
}

interface OverallScoreTip {
  studentName: string;
  displayPercentile: number;
  pct: number;
}

function OverallScoreDotPlot({ points, onOpenStudent, bandCutoffs }: OverallScoreDotPlotProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [tip, setTip] = React.useState<OverallScoreTip | null>(null);
  const AXIS_MIN = 1;
  const AXIS_MAX = 99;
  const AXIS_RANGE = AXIS_MAX - AXIS_MIN;
  const percentileToPct = (percentile: number) => ((Math.max(AXIS_MIN, Math.min(AXIS_MAX, percentile)) - AXIS_MIN) / AXIS_RANGE) * 100;
  const dotDiameter = isMobile ? 8 : 7;
  /* WCAG 2.5.8 floor: the button is a 24px invisible hit area; the
     visible dot stays small as an inner span. */
  const hitDiameter = 24;
  const trackHeight = isMobile ? 60 : 68;
  const veryLowMax = bandCutoffs.veryLowMax;
  const lowMax = bandCutoffs.lowMax;
  const veryLowDisplay = Math.round(veryLowMax);
  const lowDisplay = Math.round(lowMax);
  const laneYPcts = [50, 38, 62, 26, 74, 18, 82];
  const minSeparationPct = isMobile ? 2.2 : 1.4;
  const recentByLane: Array<{ lane: number; pct: number }> = [];
  const positionedPoints = [...points]
    .sort((a, b) => a.percentile - b.percentile || a.studentName.localeCompare(b.studentName) || a.studentId.localeCompare(b.studentId))
    .map((point) => {
      const pct = percentileToPct(point.percentile);
      const lane = laneYPcts.findIndex((_, laneIndex) =>
        !recentByLane.some((recent) => recent.lane === laneIndex && Math.abs(recent.pct - pct) < minSeparationPct),
      );
      const resolvedLane = lane >= 0 ? lane : recentByLane.length % laneYPcts.length;
      recentByLane.push({ lane: resolvedLane, pct });
      return { ...point, yPct: laneYPcts[resolvedLane] };
    });

  /* The 24px hit areas of neighbouring dots can overlap. Pointer
     interactions resolve to the dot whose CENTRE is nearest the cursor,
     so a teacher who clicks a visible dot always opens that student —
     not whichever overlapping sibling rendered last. Keyboard
     activation (click with detail === 0) keeps the focused student. */
  const trackRef = React.useRef<HTMLDivElement | null>(null);
  const nearestPoint = (clientX: number, clientY: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return null;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    /* Coordinates outside the track (assistive-tech activations can fire
       clicks with zeroed coords) → no nearest match; the caller falls back
       to the button's own student. */
    if (x < 0 || x > rect.width || y < 0 || y > rect.height) return null;
    let best: (typeof positionedPoints)[number] | null = null;
    let bestDistance = Infinity;
    for (const point of positionedPoints) {
      const px = (percentileToPct(point.percentile) / 100) * rect.width;
      const py = ((point.yPct ?? 50) / 100) * rect.height;
      const distance = (px - x) ** 2 + (py - y) ** 2;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = point;
      }
    }
    return best;
  };
  const tipFor = (point: (typeof positionedPoints)[number]) => ({
    studentName: point.studentName,
    displayPercentile: point.displayPercentile,
    pct: percentileToPct(point.percentile),
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flex: 1, position: 'relative' }}>
          {tip && (
            <div style={{
              position: 'absolute',
              left: `${tip.pct}%`,
              bottom: 'calc(100% + 6px)',
              transform: 'translateX(-50%)',
              background: 'var(--ink-800)',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              padding: '4px 9px',
              borderRadius: 6,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              zIndex: 10,
              boxShadow: 'var(--sh-2)',
              textAlign: 'center',
            }}>
              <div>{tip.studentName}</div>
              <div>ENSSA Performance PR {tip.displayPercentile}</div>
              <span style={{ position: 'absolute', left: '50%', bottom: -4, transform: 'translateX(-50%)', width: 8, height: 8, background: 'var(--ink-800)', clipPath: 'polygon(0 0,100% 0,50% 100%)' }} />
            </div>
          )}
          <div
            role="img"
            ref={trackRef}
            aria-label={`Overall ENSSA percentile distribution for ${points.length} students`}
            style={{
              position: 'relative',
              width: '100%',
              height: trackHeight,
              borderRadius: 12,
              background: `linear-gradient(to right,
                color-mix(in oklab, var(--risk-concern) 14%, white) 0%,
                color-mix(in oklab, var(--risk-concern) 14%, white) ${((veryLowMax - AXIS_MIN) / AXIS_RANGE) * 100}%,
                color-mix(in oklab, var(--risk-emerging) 14%, white) ${((veryLowMax - AXIS_MIN) / AXIS_RANGE) * 100}%,
                color-mix(in oklab, var(--risk-emerging) 14%, white) ${((lowMax - AXIS_MIN) / AXIS_RANGE) * 100}%,
                color-mix(in oklab, var(--risk-on-track) 14%, white) ${((lowMax - AXIS_MIN) / AXIS_RANGE) * 100}%,
                color-mix(in oklab, var(--risk-on-track) 14%, white) 100%)`,
              border: '1px solid var(--border-subtle)',
            }}
          >
            {positionedPoints.map((point) => (
              <button
                type="button"
                className="class-distribution-dot"
                key={`${point.studentId}-${point.percentile}-${point.yPct}`}
                aria-label={`Open ${point.studentName}'s report, ENSSA Performance PR ${point.displayPercentile}`}
                title={`Open ${point.studentName}'s report`}
                onMouseEnter={(e) => setTip(tipFor(nearestPoint(e.clientX, e.clientY) ?? point))}
                onMouseMove={(e) => setTip(tipFor(nearestPoint(e.clientX, e.clientY) ?? point))}
                onMouseLeave={() => setTip(null)}
                onFocus={() => setTip(tipFor(point))}
                onBlur={() => setTip(null)}
                onClick={(e) => {
                  /* detail === 0 → keyboard activation: honour the focused
                     student. Pointer clicks resolve to the nearest centre. */
                  const target = e.detail === 0 ? point : nearestPoint(e.clientX, e.clientY) ?? point;
                  onOpenStudent(target.studentId);
                }}
                style={{
                  position: 'absolute',
                  left: `${percentileToPct(point.percentile)}%`,
                  top: `${point.yPct}%`,
                  width: hitDiameter,
                  height: hitDiameter,
                  marginLeft: -hitDiameter / 2,
                  marginTop: -hitDiameter / 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <span
                  aria-hidden="true"
                  className="class-distribution-dot-visual"
                  style={{
                    display: 'block',
                    width: dotDiameter,
                    height: dotDiameter,
                    borderRadius: '50%',
                    background: 'var(--ink-900)',
                    pointerEvents: 'none',
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      {!isMobile && (
        <div
          style={{
            position: 'relative',
            height: 14,
            marginTop: 6,
            fontSize: 11,
            color: 'var(--fg-3)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          <span style={{ position: 'absolute', left: `${((veryLowMax - AXIS_MIN) / AXIS_RANGE) * 100}%`, transform: 'translateX(-50%)' }}>{percentileLabel(veryLowDisplay)}</span>
          <span style={{ position: 'absolute', left: `${((lowMax - AXIS_MIN) / AXIS_RANGE) * 100}%`, transform: 'translateX(-50%)' }}>{percentileLabel(lowDisplay)}</span>
        </div>
      )}

      <div
        style={{
          position: 'relative',
          height: isMobile ? 13 : 18,
          marginTop: isMobile ? 8 : 6,
          fontSize: isMobile ? 9 : 11,
          fontWeight: 700,
          letterSpacing: isMobile ? '0.02em' : '0.04em',
          textTransform: 'uppercase',
        }}
      >
        <span style={{ position: 'absolute', left: '0%', width: `${((veryLowMax - AXIS_MIN) / AXIS_RANGE) * 100}%`, display: 'flex', justifyContent: 'center', color: 'var(--risk-concern-fg)' }}>
          {isMobile ? 'V. Low' : 'Very Low'}
        </span>
        <span style={{ position: 'absolute', left: `${((veryLowMax - AXIS_MIN) / AXIS_RANGE) * 100}%`, width: `${((lowMax - veryLowMax) / AXIS_RANGE) * 100}%`, display: 'flex', justifyContent: 'center', color: 'var(--risk-emerging-fg)' }}>
          Low
        </span>
        <span style={{ position: 'absolute', left: `${((lowMax - AXIS_MIN) / AXIS_RANGE) * 100}%`, width: `${((AXIS_MAX - lowMax) / AXIS_RANGE) * 100}%`, display: 'flex', justifyContent: 'center', color: 'var(--risk-on-track-fg)' }}>
          On Track
        </span>
      </div>
    </div>
  );
}

/* ── Screener results: completion strip + bar pyramid ── */
function ScreenerResultsCard({ counts, students, term }: { counts: RiskCounts; students: Student[]; term: TermId | string }) {
  return (
    <>
      <RiskPyramid counts={counts} />
      <ScreeningCompletionStrip students={students} term={term} />
    </>
  );
}

interface ClassPageProps {
  school: School;
  klass: ClassRoom;
  onOpenStudent: (id: string, term?: TermId | string) => void;
  term?: TermId | string | undefined;
}

function ClassPage({ school, klass, onOpenStudent, term }: ClassPageProps) {
  const { track } = useTracker();
  /* Per-section term controls (matches DistrictPage / SchoolPage pattern):
     each section that surfaces term-dependent data carries its own
     TermToggle so users can compare across terms without changing the
     screener-results term above. Defaults to the page-level `term` prop
     so deep links keep working. */
  const initialTerm = term || TERM_DEFAULT;
  const [termOverall, setTermOverall] = useCS<string>(initialTerm);
  const [termTask, setTermTask] = useCS<string>(initialTerm);
  const [termStudents, setTermStudents] = useCS<string>(initialTerm);
  const [demoFilters, setDemoFilters] = useCS<DemographicFilters>(NO_DEMOGRAPHIC_FILTERS);
  const [isPrinting, setIsPrinting] = React.useState(false);

  React.useEffect(() => {
    const startPrint = () => setIsPrinting(true);
    const endPrint = () => setIsPrinting(false);
    const media = window.matchMedia?.('print');
    const handleMediaChange = (event: MediaQueryListEvent) => setIsPrinting(event.matches);

    window.addEventListener('beforeprint', startPrint);
    window.addEventListener('afterprint', endPrint);
    media?.addEventListener?.('change', handleMediaChange);

    return () => {
      window.removeEventListener('beforeprint', startPrint);
      window.removeEventListener('afterprint', endPrint);
      media?.removeEventListener?.('change', handleMediaChange);
    };
  }, []);

  const demoPredicate = useMemo(() => makeDemographicPredicate(demoFilters), [demoFilters]);
  const filteredStudents = useMemo(
    () => klass.students.filter(demoPredicate),
    [klass.students, demoPredicate],
  );
  const reportStudents = isPrinting ? klass.students : filteredStudents;

  const scoreTerms = availableTermIds(reportStudents, true);
  const taskTerms = availableTermIds(reportStudents, false);
  const resolvedTermOverall = scoreTerms.includes(termOverall) ? termOverall : defaultAvailableTermId(scoreTerms);
  const resolvedTermStudents = scoreTerms.includes(termStudents) ? termStudents : defaultAvailableTermId(scoreTerms);
  const resolvedTermTask = taskTerms.includes(termTask) ? termTask : defaultAvailableTermId(taskTerms);

  const cn: AggregateNode = { students: reportStudents };
  const counts = riskCountsOf(cn, undefined, undefined, resolvedTermStudents);
  const taskBattery = useMemo(
    () => getBattery(klass.year, klass.cohort || 'A', resolvedTermTask),
    [klass.year, klass.cohort, resolvedTermTask],
  );
  const taskDistributions = useMemo(
    () => taskFamilyDistributionsForBattery(reportStudents, taskBattery, resolvedTermTask),
    [reportStudents, taskBattery, resolvedTermTask],
  );
  const overallScorePoints = useMemo(
    () => overallScorePointsForTerm(reportStudents, resolvedTermOverall),
    [reportStudents, resolvedTermOverall],
  );
  const overallBandCutoffs = OVERALL_PERCENTILE_BAND_CUTOFFS;
  const transitions = getMultiStageTransitions(cn);
  const hasMovement = scoreTerms.length >= 2;

  function syncClassTerm(nextTerm: string, source: string) {
    track("class_term_changed", { section: source, term: nextTerm });
    if (scoreTerms.includes(nextTerm)) {
      setTermOverall(nextTerm);
      setTermStudents(nextTerm);
    }
    if (taskTerms.includes(nextTerm)) {
      setTermTask(nextTerm);
    }
  }

  return (
    <>
      <div className="print-header class-print-header">
        <div className="print-header-logo-wrap">
          <img className="print-header-logo" src="/assets/enssa-logo.png" alt="ENSSA" />
        </div>
        <div className="print-header-title-wrap">
          <h1 style={{ margin: 0 }}>Class report — {klass.name}</h1>
          <div>{school.name}{klass.teacher ? ` · ${klass.teacher}` : ''}</div>
        </div>
      </div>

      <div className="class-print-body">
      <div className="class-recommendation-print" style={{ marginBottom: 24 }}>
        <RecommendationCard counts={counts} klass={klass} />
      </div>

      <div className="class-print-hide" style={{
        background: 'var(--ink-50)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--r-md)',
        padding: '0 16px',
        marginBottom: 24,
      }}>
        <DemographicFilterBar
          value={demoFilters}
          onChange={setDemoFilters}
          totalCount={klass.students.length}
          filteredCount={filteredStudents.length}
        />
      </div>

      <section className="class-print-page-1">
      <Section title="Class Number Sense Profile" style={{ marginBottom: 20 }}>
        <Card className="class-print-compact-card">
          <ScreenerResultsCard counts={counts} students={reportStudents} term={resolvedTermStudents} />
        </Card>
      </Section>

      <Section
        title="Class Distribution of Overall ENSSA Performance (Percentile Ranks)"
        style={{ marginBottom: 20 }}
        filters={scoreTerms.length > 1 ? <span className="class-print-hide"><TermToggle value={resolvedTermOverall} availableTerms={scoreTerms} onChange={(v) => {
          if (v === resolvedTermOverall) return;
          syncClassTerm(v, "overall_score_distribution");
        }} /></span> : null}
      >
        <Card>
          <OverallScoreDotPlot
            points={overallScorePoints}
            onOpenStudent={(id) => onOpenStudent(id, resolvedTermOverall)}
            bandCutoffs={overallBandCutoffs}
          />
          <div
            style={{
              marginTop: 14,
              color: 'var(--fg-2)',
              fontSize: 12,
              lineHeight: 1.55,
            }}
          >
            {OVERALL_PERCENTILE_SCALE_NOTE}
            <div style={{ marginTop: 6, opacity: 0.82 }}><PercentileCutoffFootnote /></div>
          </div>
        </Card>
      </Section>

      {hasMovement && (
        <Section title="How Students in this Class Performed Over Time">
          <Card><RiskTransitionSankey transitions={transitions} classId={klass.id} onOpenStudent={onOpenStudent} /></Card>
        </Section>
      )}

      </section>

      <section className="class-print-page-2">
      <Section title={`Class Performance by ${PORTAL_TERMS.task}`} style={{ marginBottom: 20 }}>
        <Card>
          {taskTerms.length > 1 && (
            <div className="class-print-hide" style={{ marginBottom: 14 }}>
              <TermToggle value={resolvedTermTask} availableTerms={taskTerms} onChange={(v) => {
                if (v === resolvedTermTask) return;
                syncClassTerm(v, "task_profile");
              }} />
            </div>
          )}

          <ClassTaskFamilyTable
            distributions={taskDistributions}
            focusTaskIds={klass.classRecommendationFocusTaskIds ?? []}
            recommendationHref={classRecommendationHref(klass)}
            onTeachingFocusOpen={(taskId) => track("class_recommendation_opened", classRecommendationTrackingProps(klass, "subtest_focus_chip", taskId))}
            onOpenStudent={(id) => onOpenStudent(id, resolvedTermTask)}
          />
        </Card>
      </Section>
      </section>

      <Section
        title="Individual Student Results"
        hint={<span className="class-print-hide">Click a row to expand task scores inline, or open the full report.</span>}
        controls={<span className="class-print-hide"><CSVExportButton scope={undefined} onExport={() => downloadCsv(`${klass.id}-${resolvedTermStudents}-student-results.csv`, classStudentCsvRows(klass, klass.students, resolvedTermStudents))} /></span>}
        style={{ paddingTop: 18 }}
      >
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <StudentTable
            students={reportStudents}
            klass={klass}
            onOpenStudent={onOpenStudent}
            term={resolvedTermStudents}
            availableTerms={scoreTerms}
            isPrinting={isPrinting}
            onTermChange={(v) => {
              if (v === resolvedTermStudents) return;
              syncClassTerm(v, "student_table");
            }}
          />
        </Card>
      </Section>
      </div>
    </>);

}

interface ScreeningCompletionStripProps {
  students: Student[];
  term: TermId | string;
}

/* ── Compact screening completion strip within the profile card ── */
function ScreeningCompletionStrip({ students, term }: ScreeningCompletionStripProps) {
  const status = students.reduce<StatusCounts>((counts, student) => {
    const assessment = getStudentAssessment(student, term);
    counts[assessment.status] += 1;
    counts.total += 1;
    return counts;
  }, { completed: 0, partial: 0, notstarted: 0, total: 0 });

  return (
    <div style={{ margin: '18px 0 0', display: 'flex', gap: 16, flexWrap: 'wrap' }} aria-label="Screening completion">
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}><StatusBadge status="completed" /> <strong style={{ fontFamily: 'var(--font-mono)' }}>{status.completed}</strong></div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}><StatusBadge status="partial" /> <strong style={{ fontFamily: 'var(--font-mono)' }}>{status.partial}</strong></div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}><StatusBadge status="notstarted" /> <strong style={{ fontFamily: 'var(--font-mono)' }}>{status.notstarted}</strong></div>
    </div>
  );
}

type ViewKey = 'all' | 'high' | 'moderate' | 'low';

interface ViewSelectorProps {
  value: ViewKey;
  onChange: (v: ViewKey) => void;
}

/* ── Class recommendation card ── */
function RecommendationCard({ counts, klass }: { counts: RiskCounts; klass: ClassRoom }) {
  const { track } = useTracker();
  const classifiedTotal = counts.high + counts.moderate + counts.low;
  const belowExpectedPct = classifiedTotal > 0
    ? Math.round(((counts.high + counts.moderate) / classifiedTotal) * 100)
    : 0;
  const hasFocusAreas = (klass.classRecommendationFocusTaskIds?.length ?? 0) > 0;
  const recommendationHref = classRecommendationHref(klass);
  return (
    <Card>
      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 16px' }}>Class Recommendation</h2>
      <div style={{ fontSize: 15, color: 'var(--fg-2)', lineHeight: 1.65, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ margin: 0 }}>
          {classifiedTotal === 0 ? (
            'No students in this class are currently classified in the Very Low or Low range.'
          ) : (
            <>
              There are <strong style={{ color: 'var(--fg-1)' }}>{belowExpectedPct}%</strong> of students performing
              below expected levels in foundational number sense skills linked to later mathematics learning difficulties.
            </>
          )}
        </p>
        <p style={{ margin: 0 }}>
          {hasFocusAreas ? 'Teaching resources aligned to this class profile can be accessed at ' : 'Some students may benefit from targeted teaching resources at '}
          <a
            href={recommendationHref}
            target="_blank"
            rel="noreferrer"
            onClick={() => track("class_recommendation_opened", classRecommendationTrackingProps(klass, "recommendation_card"))}
            style={{ color: 'var(--risk-on-track-fg)', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 2 }}
          >https://enssa.app</a>.
        </p>
      </div>
    </Card>);
}

/* ── Simple view selector replacing the old FilterPanel ── */
function ViewSelector({ value, onChange }: ViewSelectorProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const opts = [
    { id: 'all',        label: 'Show all' },
    { id: 'high',       label: isMobile ? 'V low' : riskLabel('high') },
    { id: 'moderate',   label: riskLabel('moderate') },
    { id: 'low',        label: riskLabel('low') },
  ] as const satisfies ReadonlyArray<{ id: ViewKey; label: string }>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Performance</span>
      <ToggleGroup
        type="single"
        size="sm"
        aria-label="Filter view by performance band"
        value={value}
        onValueChange={(v) => { if (v) onChange(v as ViewKey) }}
        style={{ background: '#fff', borderRadius: 7, padding: 2, border: '1px solid var(--border-default)', flexWrap: 'wrap' }}
      >
        {opts.map(o => (
          <ToggleGroupItem
            key={o.id}
            value={o.id}
            className={`view-toggle-item${value === o.id ? ' is-active' : ''}`}
            style={{
              background: value === o.id ? 'var(--enssa-aubergine)' : 'transparent',
              color: value === o.id ? '#fff' : 'var(--fg-1)',
              padding: '6px 12px',
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 6,
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
              height: 'auto',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease, color 0.15s ease, transform 0.15s ease',
            }}
          >
            {o.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}

type SortKey = 'name' | 'compositeScore' | 'risk' | 'overallPercentile' | 'status';

interface StudentTableProps {
  students: Student[];
  klass: ClassRoom;
  onOpenStudent: (id: string, term?: TermId | string) => void;
  term: TermId | string;
  availableTerms: readonly (TermId | string)[];
  onTermChange: (next: TermId) => void;
  isPrinting?: boolean;
}

function StudentTable({ students, klass, onOpenStudent, term, availableTerms, onTermChange, isPrinting = false }: StudentTableProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const { track } = useTracker();
  const [sortKey, setSortKey] = useCS<SortKey>('compositeScore');
  const [asc, setAsc] = useCS(false);
  const [openId, setOpenId] = useCS<string | null>(null);
  const [view, setView] = useCS<ViewKey>('all');

  function getPercentile(a: AssessmentForTerm): number | null {
    if (a.risk === 'more_info') return null;
    return a.overallPercentile;
  }


  const withAssessment = useMemo(
    () => students.map(s => ({ s, a: getStudentAssessment(s, term) })),
    [students, term],
  );

  const filtered = useMemo(() => withAssessment.filter(({ a }) => {
    if (isPrinting) return true;
    if (view === 'high')       return a.risk === 'high';
    if (view === 'moderate')   return a.risk === 'moderate';
    if (view === 'low')        return a.risk === 'low';
    return true;
  }), [withAssessment, view, isPrinting]);

  const sorted = useMemo(() => {
    const statusOrder: Record<AssessmentForTerm['status'], number> = {
      completed: 2,
      partial: 1,
      notstarted: 0,
    };
    const statusProgress = (a: AssessmentForTerm) => {
      const ratio = a.tasksTotal > 0 ? a.tasksDone / a.tasksTotal : 0;
      return ratio * 10 + statusOrder[a.status];
    };
    return [...filtered].sort((p, q) => {
      let cmp: number;
      if (sortKey === 'name') {
        cmp = p.s.name.localeCompare(q.s.name);
      } else if (sortKey === 'status') {
        cmp = statusProgress(p.a) - statusProgress(q.a);
      } else {
        const av = (p.a as unknown as Record<string, unknown>)[sortKey];
        const bv = (q.a as unknown as Record<string, unknown>)[sortKey];
        if (typeof av === 'string' && typeof bv === 'string') cmp = av.localeCompare(bv);
        else cmp = (Number(av) || 0) - (Number(bv) || 0);
      }
      return asc ? cmp : -cmp;
    });
  }, [filtered, sortKey, asc]);

  function printStatusText(a: AssessmentForTerm): string {
    if (a.status === 'completed') return 'Completed';
    if (a.status === 'partial') return 'Partial';
    return 'Not started';
  }

  function header(key: SortKey, label: string, align: 'left' | 'right' | 'center' = 'left') {
    return (
      <TableHead
        onClick={() => {
          const nextDirection = sortKey === key ? (!asc ? "asc" : "desc") : "desc";
          if (sortKey === key) setAsc(!asc);
          else {
            setSortKey(key);
            setAsc(false);
          }
          track("class_student_table_sorted", { sort_key: key, direction: nextDirection });
        }}
        aria-sort={sortKey === key ? (asc ? 'ascending' : 'descending') : 'none'}
        scope="col"
        style={{ textAlign: align, padding: '14px 16px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--fg-3)', fontWeight: 700, cursor: 'pointer', userSelect: 'none', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap', height: 'auto' }}>
        {label}{!isPrinting && sortKey === key ? <span className="class-print-hide">{asc ? ' ↑' : ' ↓'}</span> : null}
      </TableHead>
    );
  }

  // Row click handler differs by viewport: mobile = navigate, desktop/tablet = expand.
  const handleRowClick = (s: Student) => {
    if (isPrinting) return;
    if (isMobile) {
      track("student_opened", { student_id: s.id, source: "mobile_row" });
      onOpenStudent(s.id, term);
      return;
    }

    const nextOpenId = openId === s.id ? null : s.id;
    setOpenId(nextOpenId);
    if (nextOpenId) {
      track("class_student_row_expanded", { student_id: s.id });
    }
  };

  // colSpan for the inline "Task profile" expansion row.
  // Desktop+tablet: blank | Student | Performance | Score | %ile | Status | View → = 7 cells
  // First cell is the chevron, then we colSpan the remaining 6.
  const expandColSpan = 6;

  return (
    <div>
      <div className="class-print-hide" style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: 'var(--ink-50)',
        flexWrap: 'wrap',
      }}>
        <ViewSelector value={view} onChange={(nextView) => {
          if (nextView === view) return;
          track("class_view_filter_changed", { filter: nextView });
          setView(nextView);
        }} />
        {availableTerms.length > 1 && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <TermToggle value={term} availableTerms={availableTerms} onChange={onTermChange} />
          </div>
        )}
      </div>
      <Table style={{ borderCollapse: 'collapse', minWidth: isMobile ? 0 : 680 }}>
        <TableHeader style={{
          position: 'sticky',
          top: 0,
          zIndex: 19,
          background: 'var(--ink-50)',
          boxShadow: '0 2px 6px color-mix(in oklab, var(--ink-900) 6%, transparent)',
        }}>
          <TableRow>
            {!isMobile && <TableHead className="class-print-hide" style={{ width: 36, borderBottom: '1px solid var(--border-subtle)', height: 'auto' }} aria-hidden="true"></TableHead>}
            {header('name', 'Student')}
            {isMobile ? (
              header('compositeScore', 'Performance / Score', 'left')
            ) : (
              <>
                {header('risk', 'Performance')}
                {header('compositeScore', 'ENSSA Standard Score', 'center')}
              </>
            )}
            {header('overallPercentile', 'Percentile Rank', 'center')}
            {header('status', 'Status', 'center')}
            <TableHead className="class-print-hide" style={{ borderBottom: '1px solid var(--border-subtle)', height: 'auto' }} aria-hidden="true"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map(({ s, a }) => {
            const open = !isPrinting && !isMobile && openId === s.id;
            const pctile = getPercentile(a);
            return (
              <React.Fragment key={s.id}>
                <TableRow
                  style={{ borderBottom: '1px solid var(--border-subtle)', background: open ? 'var(--ink-50)' : 'transparent', cursor: 'pointer' }}
                  aria-expanded={!isMobile ? open : undefined}
                  onClick={() => handleRowClick(s)}>
                  {!isMobile && (
                    <TableCell
                      className="class-print-hide"
                      aria-hidden="true"
                      style={{
                        padding: '12px 0 12px 16px',
                        color: 'var(--fg-2)',
                        fontSize: 16,
                        lineHeight: 1,
                        width: 36,
                      }}
                    >
                      {open ? '▾' : '▸'}
                    </TableCell>
                  )}
                  <TableCell style={{ padding: '14px 16px', fontWeight: 600 }}>{s.name}</TableCell>
                  {isMobile ? (
                    <TableCell style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'nowrap' }}>
                        <RiskBadge level={a.risk} size="sm" />
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 18, color: a.risk === 'more_info' ? 'var(--fg-3)' : 'var(--fg-1)', flexShrink: 0 }}>
                          {a.risk === 'more_info' ? '—' : a.compositeScore}
                        </span>
                      </div>
                    </TableCell>
                  ) : (
                    <>
                      <TableCell style={{ padding: '14px 16px' }}><RiskBadge level={a.risk} size="sm" /></TableCell>
                      <TableCell style={{ padding: '14px 16px', fontFamily: 'var(--font-mono)', textAlign: 'center', fontWeight: 700 }}>{a.risk === 'more_info' ? '—' : a.compositeScore}</TableCell>
                    </>
                  )}
                  <TableCell style={{ padding: '14px 16px', textAlign: 'center' }}>
                    {isMobile ? (pctile != null ? percentileRankLabel(pctile) : '—') : <PercentileRankMiniBar percentile={pctile} pendingLabel="—" />}
                  </TableCell>
                  <TableCell style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 13, color: 'var(--fg-2)', whiteSpace: 'normal', width: '100%' }}>
                      <span
                        aria-hidden="true"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '1.1em',
                          flexShrink: 0,
                          fontWeight: 800,
                          lineHeight: 1,
                          color: 'var(--fg-2)',
                        }}
                      >
                        {a.status === 'completed' ? '✓' : a.status === 'partial' ? '◐' : '○'}
                      </span>
                      {isPrinting ? printStatusText(a) : screenerStatusText(a)}
                    </span>
                  </TableCell>
                  <TableCell className="class-print-hide" style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <button onClick={(e) => {
                      e.stopPropagation();
                      track("student_opened", {
                        student_id: s.id,
                        source: isMobile ? "mobile_row" : "desktop_view_button",
                      });
                      onOpenStudent(s.id, term);
                    }}
                    className="class-row-view-btn"
                    style={{
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-default)',
                      color: 'var(--risk-on-track-fg)',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontWeight: 700,
                      fontSize: 13,
                      whiteSpace: 'nowrap',
                      padding: '6px 12px',
                      borderRadius: 8,
                      lineHeight: 1.2,
                    }}>
                      View →
                    </button>
                  </TableCell>
                </TableRow>
                {open &&
                <TableRow className="class-print-hide" style={{ background: 'var(--ink-50)', borderBottom: '2px solid var(--border-default)' }}>
                    <TableCell></TableCell>
                    <TableCell colSpan={expandColSpan} style={{ padding: '14px 16px 22px', whiteSpace: 'normal' }}>
                      <StudentSubtestCards student={s} klass={klass} term={term} />
                    </TableCell>
                  </TableRow>
                }
              </React.Fragment>);

          })}
        </TableBody>
      </Table>
    </div>);

}

export { ClassPage };
export default ClassPage;
